// src/components/messaging/FloatingMessaging/FloatingMessagingManager.jsx - Version corrigée
import React, { useEffect, useState, useMemo, useRef } from 'react'
import { useFloatingMessaging } from '../../hooks/useFloatingMessaging'
import FloatingMessagingDock from './FloatingMessagingDock'
import FloatingConversationWindow from './FloatingConversationWindow'
import './FloatingMessagingManager.scss'

const FloatingMessagingManager = () => {
  // SEUL endroit où useFloatingMessaging est appelé
  const messagingState = useFloatingMessaging()

  const { isDockOpen, openConversations, closeConversation, toggleMinimizeConversation } =
    messagingState

  const [windowPositions, setWindowPositions] = useState(new Map())
  const [isMobile, setIsMobile] = useState(false)

  const lastConversationStateRef = useRef('')
  const lastMobileStateRef = useRef(false)

  // Détecter les changements de taille d'écran
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth <= 768
      setIsMobile(mobile)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  const conversationSignature = useMemo(() => {
    return openConversations
      .map((conv) => `${conv.id}-${conv.isMinimized ? 'min' : 'max'}`)
      .join(',')
  }, [openConversations])

  useEffect(() => {
    const currentSignature = conversationSignature
    const currentMobile = isMobile

    if (
      currentSignature === lastConversationStateRef.current &&
      currentMobile === lastMobileStateRef.current
    ) {
      return
    }

    lastConversationStateRef.current = currentSignature
    lastMobileStateRef.current = currentMobile

    const timeoutId = setTimeout(() => {
      const newPositions = new Map()

      if (isMobile) {
        if (openConversations.length > 0) {
          newPositions.set(openConversations[0].id, {
            right: '0px',
            left: '0px',
            top: '0px',
            bottom: '0px',
            zIndex: 10000,
            fullscreen: true,
          })
        }
        setWindowPositions(newPositions)
        return
      }

      const screenWidth = window.innerWidth
      const windowWidth = Math.min(380, screenWidth * 0.25)
      const windowSpacing = 20
      const rightMargin = 20
      const dockWidth = Math.min(380, screenWidth * 0.25)
      const totalWidthNeeded =
        rightMargin +
        dockWidth +
        windowSpacing +
        openConversations.length * (windowWidth + windowSpacing)

      openConversations.forEach((conversation, index) => {
        let rightOffset

        if (totalWidthNeeded > screenWidth) {
          rightOffset =
            rightMargin + dockWidth + windowSpacing + index * Math.min(windowSpacing * 2, 60)
        } else {
          rightOffset =
            rightMargin + dockWidth + windowSpacing + index * (windowWidth + windowSpacing)
        }

        newPositions.set(conversation.id, {
          right: `${rightOffset}px`,
          zIndex: 9998 - index,
          width: `${windowWidth}px`,
        })
      })

      setWindowPositions(newPositions)
    }, 0)

    return () => clearTimeout(timeoutId)
  }, [conversationSignature, isMobile, openConversations])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && openConversations.length > 0) {
        const mostRecentConversation = openConversations[0]
        if (mostRecentConversation && !mostRecentConversation.isMinimized) {
          closeConversation(mostRecentConversation.id)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [openConversations.length, closeConversation])

  const maxConversations = isMobile ? 1 : 3
  const visibleConversations = openConversations.slice(0, maxConversations)
  const hiddenConversationsCount = Math.max(0, openConversations.length - maxConversations)

  return (
    <div className="floating-messaging-manager-premium">
      {/* Dock avec state passé en props */}
      <FloatingMessagingDock messagingState={messagingState} />

      {/* Indicateur pour les conversations masquées */}
      {!isMobile && hiddenConversationsCount > 0 && (
        <div className="hidden-conversations-indicator">
          <div className="indicator-content">
            <span className="indicator-text">
              +{hiddenConversationsCount} conversation{hiddenConversationsCount > 1 ? 's' : ''}
            </span>
            <span className="indicator-hint">Fermez une fenêtre pour voir les autres</span>
          </div>
        </div>
      )}

      {/* Fenêtres de conversation */}
      {visibleConversations.map((conversation, index) => {
        const position = windowPositions.get(conversation.id) || {
          right: isMobile ? '0px' : `${20 + Math.min(380, window.innerWidth * 0.25) + 20}px`,
          zIndex: 9998,
          width: isMobile ? '100vw' : `${Math.min(380, window.innerWidth * 0.25)}px`,
        }

        const conversationStyle = isMobile
          ? {
              position: 'fixed',
              top: '0px',
              left: '0px',
              right: '0px',
              bottom: '0px',
              width: '100vw',
              height: '100vh',
              zIndex: position.zIndex,
            }
          : {
              position: 'fixed',
              bottom: '0px',
              right: position.right,
              zIndex: position.zIndex,
              width: position.width || '380px',
            }

        return (
          <div
            key={conversation.id}
            className={`conversation-window-container ${conversation.isMinimized ? 'minimized' : 'expanded'} ${isMobile ? 'mobile-fullscreen' : ''}`}
            style={conversationStyle}
          >
            <FloatingConversationWindow
              conversation={conversation}
              isMinimized={conversation.isMinimized}
              messagingState={messagingState}
              onClose={() => closeConversation(conversation.id)}
              onMinimize={() => toggleMinimizeConversation(conversation.id)}
            />
          </div>
        )
      })}

      {/* Overlay pour mobile */}
      {isMobile && openConversations.length > 0 && <div className="mobile-conversations-overlay" />}
    </div>
  )
}

export default FloatingMessagingManager
